import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import endPoints from '@/redux/constant';
import axios from '@/redux/http';

const initialState = {
  loading: false,
  error: null,
  users: [],
  totalCount: 0,
  page: 1,
  pageSize: 10,
  paginate: true,
  search: '',
  sortExpression: '',
  deleteLoadingId: null,
  updateLoadingId: null,
  userEvents: [],
  userEventsUserId: null,
  userEventsLoading: false,
  userEventsError: null,
  allUserEvents: { dashboard: [], lastVisit: [], sectionsOpened: [], sampleMediaKitLoad: [] },
  allUserEventsLoading: false,
  allUserEventsError: null,
};

export const getAllUsersAPIFn = createAsyncThunk(
  'adminUsers/getAllUsers',
  async (
    {
      page = 1,
      pageSize = 10,
      paginate = true,
      search = '',
      sortExpression = '',
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const trimmedSearch = search?.trim?.() ?? '';
      const params = {
        Paginate_ysn: paginate,
        Page_Index_int: page,
        Page_Size_int: pageSize,
      };

      if (trimmedSearch) {
        params.Where_GeneralCriteria_Delim_mem = trimmedSearch;
      }
      if (sortExpression) {
        params.Sort_Expression_Delim_mem = sortExpression;
      }

      const { data } = await axios.get(endPoints.getAllUsersURL, { params });
      return {
        users: data?.users ?? [],
        totalCount: data?.totalCount ?? 0,
        page,
        pageSize,
        paginate,
        search: trimmedSearch,
        sortExpression: sortExpression || '',
      };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error?.response?.data || 'Failed to load users'
      );
    }
  }
);

export const deleteUserAPIFn = createAsyncThunk(
  'adminUsers/deleteUser',
  async ({ userId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${endPoints.deleteUserURL}?userId=${userId}`);
      return data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error?.response?.data || 'Failed to delete user'
      );
    }
  }
);

export const updateUserAPIFn = createAsyncThunk(
  'adminUsers/updateUser',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(endPoints.updateUserURL, payload);
      return { userId: payload.userId, data };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error?.response?.data || 'Failed to update user'
      );
    }
  }
);

export const getUserEventsAPIFn = createAsyncThunk(
  'adminUsers/getUserEvents',
  async ({ userId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${endPoints.getUserEvents}?userId=${userId}`);
      return { userId, events: data?.result?.dashboard ?? data?.result ?? data ?? [] };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error?.response?.data || 'Failed to load user activity'
      );
    }
  }
);

const DASHBOARD_KEY_ORDER = ['Returning User', 'Direct Signup', 'WordPress Claim'];

const hasDashboardKey = group =>
  typeof group?.dashboardKey === 'string' && group.dashboardKey.trim().length > 0;

const byDashboardKeyOrder = (a, b) =>
  DASHBOARD_KEY_ORDER.indexOf(a?.dashboardKey) - DASHBOARD_KEY_ORDER.indexOf(b?.dashboardKey);

export const getAllUserEventsAPIFn = createAsyncThunk(
  'adminUsers/getAllUserEvents',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(endPoints.getUserEvents);
      const result = data?.result ?? {};

      return {
        dashboard: (result.dashboard ?? []).filter(hasDashboardKey).sort(byDashboardKeyOrder),
        lastVisit: result.lastVisit ?? [],
        sectionsOpened: result.sectionsOpened ?? [],
        sampleMediaKitLoad: result.sampleMediaKitLoad ?? [],
      };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error?.response?.data || 'Failed to load activity'
      );
    }
  }
);

export const adminUsersSlice = createSlice({
  name: 'adminUsers',
  initialState,
  reducers: {
    setUsersPage: (state, action) => {
      state.page = action.payload;
    },
    setUsersPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.page = 1;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getAllUsersAPIFn.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllUsersAPIFn.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.users;
        state.totalCount = action.payload.totalCount;
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
        state.paginate = action.payload.paginate;
        state.search = action.payload.search ?? '';
        state.sortExpression = action.payload.sortExpression ?? '';
        state.error = null;
      })
      .addCase(getAllUsersAPIFn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load users';
        state.users = [];
      })
      .addCase(deleteUserAPIFn.pending, (state, action) => {
        state.deleteLoadingId = action.meta.arg.userId;
      })
      .addCase(deleteUserAPIFn.fulfilled, state => {
        state.deleteLoadingId = null;
      })
      .addCase(deleteUserAPIFn.rejected, state => {
        state.deleteLoadingId = null;
      })
      .addCase(updateUserAPIFn.pending, (state, action) => {
        state.updateLoadingId = action.meta.arg.userId;
      })
      .addCase(updateUserAPIFn.fulfilled, state => {
        state.updateLoadingId = null;
      })
      .addCase(updateUserAPIFn.rejected, state => {
        state.updateLoadingId = null;
      })
      .addCase(getUserEventsAPIFn.pending, (state, action) => {
        state.userEventsLoading = true;
        state.userEventsError = null;
        state.userEventsUserId = action.meta.arg.userId;
      })
      .addCase(getUserEventsAPIFn.fulfilled, (state, action) => {
        state.userEventsLoading = false;
        state.userEvents = action.payload.events;
        state.userEventsUserId = action.payload.userId;
      })
      .addCase(getUserEventsAPIFn.rejected, (state, action) => {
        state.userEventsLoading = false;
        state.userEvents = [];
        state.userEventsError = action.payload || 'Failed to load user activity';
      })
      .addCase(getAllUserEventsAPIFn.pending, state => {
        state.allUserEventsLoading = true;
        state.allUserEventsError = null;
      })
      .addCase(getAllUserEventsAPIFn.fulfilled, (state, action) => {
        state.allUserEventsLoading = false;
        state.allUserEvents = action.payload;
      })
      .addCase(getAllUserEventsAPIFn.rejected, (state, action) => {
        state.allUserEventsLoading = false;
        state.allUserEvents = { dashboard: [], lastVisit: [], sectionsOpened: [], sampleMediaKitLoad: [] };
        state.allUserEventsError = action.payload || 'Failed to load activity';
      });
  },
});

export const { setUsersPage, setUsersPageSize } = adminUsersSlice.actions;
export default adminUsersSlice.reducer;
